import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { TycoonMode } from './modes/TycoonMode';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastContainer, useToast, MobileLanding, RotateDevice } from '@smartphone-tycoon/shared-ui';
import { devModeService } from './services/devModeService';
import { DevModeToggle } from './components/devmode/DevModeToggle';
import { DevModeReference } from './components/devmode/DevModeReference';
import { initializeDevModeSubscription } from './stores/devModeStore';
import { clearDatabase } from './utils/clearDatabase';
import { isMobile } from '@smartphone-tycoon/shared-utils';
import { useDeviceType } from './hooks/useDeviceType';

interface GameState {
  companyState?: Record<string, unknown>;
  [key: string]: unknown;
}

export default function App() {
  const [, setGameState] = useState<GameState | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [showDevReference, setShowDevReference] = useState(false);
  const { messages, showToast, dismissToast } = useToast();
  const { isTablet, isPortrait } = useDeviceType();

  // Initialize database and stores
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Check if we need to clear the database (version mismatch)
        const dbNeedsClearing = localStorage.getItem('dbNeedsClearing');
        if (dbNeedsClearing === 'true') {
          console.log('Clearing database due to version mismatch...');
          await clearDatabase();
          localStorage.removeItem('dbNeedsClearing');
        }

        // Set up toast callback for dev mode service
        devModeService.setToastCallback(showToast);

        // Initialize devMode store subscription after React has mounted
        const unsubscribe = initializeDevModeSubscription();

        setIsInitializing(false);

        // Cleanup on unmount
        return () => {
          if (unsubscribe) unsubscribe();
        };
      } catch (error) {
        console.error('Failed to initialize app:', error);
        showToast({
          type: 'error',
          title: 'Initialization Error',
          message: 'Failed to initialize the application. Please refresh the page.'
        });
        setIsInitializing(false);
      }
    };

    initializeApp();
  }, [showToast]);

  // Check if mobile device - must be after all hooks
  if (isMobile()) {
    return <MobileLanding />;
  }

  // Check if tablet in portrait mode - prompt to rotate
  if (isTablet && isPortrait) {
    return <RotateDevice />;
  }
  
  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-green-900 to-teal-900 flex items-center justify-center">
        <div className="text-white text-center">
          <h1 className="text-2xl font-bold mb-4">Initializing...</h1>
          <p className="text-sm opacity-75">Setting up game database</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Router>
        <Routes>
          <Route 
            path="/*" 
            element={
              <TycoonMode 
                onBack={() => {}} // No-op since we don't have mode switching
                onDesignerStudio={() => {}} // No-op since designer is a separate app
                onCommunityHub={() => {}} // No-op since community is a separate app
                onDevMode={() => setShowDevReference(true)}
                onGameStateUpdate={setGameState}
              />
            } 
          />
        </Routes>
      </Router>
      
      {/* Dev Mode Toggle (F12 key handler) */}
      <DevModeToggle />
      
      {/* Dev Mode Reference Modal */}
      <DevModeReference isOpen={showDevReference} onClose={() => setShowDevReference(false)} />
      
      {/* Toast Notifications */}
      <ToastContainer messages={messages} onDismiss={dismissToast} />
    </ErrorBoundary>
  );
}